import { getPreferredPhone, getProfilePhoto, useGetAllContacts, useGetContact } from "@api/contactService";
import React, { useContext, useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ObjectShow from "@components/objects/ObjectShow";
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import ObjectColumn from "@components/objects/ObjectColumn";
import { ObjectPrimaryInfo } from "@components/objects/ObjectPrimaryInfo";
import { ObjectRelationship } from "@components/objects/ObjectRelationship";
import Avatar from "@mui/joy/Avatar";
import { ObjectContext } from "@providers/ObjectProvider";
import { ensureDateObject, formatAbsoluteDate, formatDate, isValidDate } from "@utils/dates";
import ObjectPreview from "@components/objects/ObjectPreview";
import { generateAddress } from "@utils/generateAddress";
import { CardOverflow, List, ListDivider, ListItem, ListItemContent, ListItemDecorator, Table } from "@mui/joy";
import AbstractObjectRelationship from "@components/objects/AbstractObjectRelationship";
import UserAccessCard from "@components/objects/UserAccessCard";
import { intervalToDuration } from "date-fns";
import { CheckPermission } from "@components/CheckPermission";
import SendToModal from "@components/reports/SendToModal";
import { formatUrl } from "@utils/formatUrl";

/*
interface ShowData {
    label?: string;
    conditional?: Function|boolean;
    content?: Function|string;
};
*/

const CardHeader = () => {
    const data = useContext(ObjectContext);
    const avatarInitials = data.name ? data.name.split(' ').map(i => i.charAt(0)).join('').toUpperCase() : '';

    return (
    <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={2}
        p={1.5}
        pt={0}
    >
        <Avatar size="lg" sx={{'--Avatar-size': '5rem'}} src={getProfilePhoto(data)?.url}>{avatarInitials}</Avatar>
        <Box sx={{flex: 1}}>
            <Typography fontSize={'md'} fontWeight={'bold'}>{data.name}</Typography>
            {data.title && <Typography fontSize={'sm'}>{data.title}</Typography>}
            {data.company_name && data.name !== data.company_name && <Typography fontSize={'sm'}>{data.company_name}</Typography>}
        </Box>
    </Stack>
)
};

const dataToDisplay/*: readonly ShowData[]*/ = [
    {
        label: 'Communications',
        // conditional: true,
        content: (object) => object.do_not_contact ? 'Do Not Contact' : 'Okay to Contact',
    },
    {
        label: 'Email',
        conditional: object => !!object.email,
        content: (object) => <><a href={`mailto:${object.email}`}>{object.email}</a>{object.preferred_contact_method == 'email' && <Tooltip title={'This person prefers to be contacted via email'}><Typography> (Preferred)</Typography></Tooltip>}</>,
    },
    {
        label: 'Alternate Email(s)',
        conditional: true,
        content: 'alternate_email',
    },
    {
        label: 'Phone',
        conditional: object => !!object.work_phone || !!object.cell_phone || !!object.home_phone,
        content: (object) => {
            let numbers = [];
            if(!!object.work_phone) {
                numbers.push({
                    label: 'Work',
                    preferred: object.preferred_contact_method == 'work',
                    column: 'work_phone',
                });
            }
            if(!!object.cell_phone) {
                numbers.push({
                    label: 'Cell',
                    preferred: object.preferred_contact_method == 'cell',
                    column: 'cell_phone',
                });
            }
            if(!!object.home_phone) {
                numbers.push({
                    label: 'Work',
                    preferred: object.preferred_contact_method == 'home',
                    column: 'home_phone',
                });
            }
            return (<>
                <List
                    variant="plain"
                    size="sm"
                    sx={theme => ({
                        // width: maxWidth,
                        // maxHeight: maxHeight,
                        // height: '100%',
                        // overflowY: 'auto',
                        // borderRadius: 'sm',
                        // backgroundColor: theme.palette.background,
                        // '--ListItemDecorator-size': '1.5rem',
                        '--ListItem-minHeight': '1.4rem',
                        // py: 0,
                    })}
                >
                {numbers.map((num, index) => (<React.Fragment key={index}>
                    <ListItem sx={{gap: 1.5, p: 0}}>
                        <ListItemDecorator>
                            <Typography fontSize={'xs'} fontWeight={'bold'}>{num.label}</Typography>
                        </ListItemDecorator>
                        <ListItemContent>
                            <Typography fontSize={'sm'}>
                                <a href={`tel:${object[num.column]}`}>{object[num.column]}</a>
                                {num.preferred && <Tooltip title={`This person prefers to be contacted via ${num.column.replace('_', ' ')}`}><Typography> (Preferred)</Typography></Tooltip>}
                            </Typography>
                            </ListItemContent>
                    </ListItem>
                    {/* {index < numbers.length - 1 && (<ListDivider/>)} */}
                </React.Fragment>
                ))}
                </List>
            </>)
        },
    },
    {
        label: 'Birthday',
        conditional: object => isValidDate(object.birthday),
        content: (object) => {
            const dateObj = ensureDateObject(object.birthday);
            const dateComparison = intervalToDuration({start: dateObj, end: new Date});
            return (<>
                {formatAbsoluteDate(object.birthday)} ({dateComparison.years} years)</>
            )
        },
    },
    {
        label: 'Deceased',
        conditional: object => isValidDate(object.deceased),
        content: (object) => <>{formatAbsoluteDate(object.deceased)}</>,
    },
    {
        label: 'Spouse',
        conditional: object => !!object.spouse_id,
        content: (object) => <ObjectPreview to={`/contacts/${object.spouse_id}`} object={object?.spouse}>{object?.spouse?.name ?? 'N/A'}</ObjectPreview>,
    },
    {
        label: 'Anniversary',
        conditional: object => isValidDate(object.anniversary),
        // content: (object) => <>{formatAbsoluteDate(object.anniversary)}</>,
        content: (object) => {
            const dateObj = ensureDateObject(object.anniversary);
            const dateComparison = intervalToDuration({start: dateObj, end: new Date});
            return (<>
                {formatAbsoluteDate(object.anniversary)} ({dateComparison.years} years)</>
            )
        },
    },
    {
        label: 'Address',
        conditional: object => !!object.address,
        content: (object) => <span dangerouslySetInnerHTML={{__html: generateAddress(object)}}></span>,
    },
    /*
    {
        label: 'Website',
        conditional: object => !!object.url,
        content: (object) => <a href={formatUrl(object.url)} target="_blank">{object.url}</a>,
    },
    */
    {
        label: 'Facebook',
        conditional: object => !!object.facebook_url,
        content: (object, label) => <a href={formatUrl(object.facebook_url)} target="_blank">View {label}</a>,
    },
    {
        label: 'Instagram',
        conditional: object => !!object.instagram_url,
        content: (object, label) => <a href={formatUrl(object.instagram_url)} target="_blank">View {label}</a>,
    },
    /*
    {
        label: 'X (Twitter)',
        conditional: object => !!object.twitter_url,
        content: (object, label) => <a href={formatUrl(object.twitter_url)} target="_blank">View {label}</a>,
    },
    */
    // google plus
    {
        label: 'LinkedIn',
        conditional: object => !!object.linkedin_url,
        content: (object, label) => <a href={formatUrl(object.linkedin_url)} target="_blank">View {label}</a>,
    },
    /*
    {
        label: 'YouTube',
        conditional: object => !!object.youtube_url,
        content: (object, label) => <a href={formatUrl(object.youtube_url)} target="_blank">View {label}</a>,
    },
    {
        label: 'Yelp',
        conditional: object => !!object.yelp_url,
        content: (object, label) => <a href={formatUrl(object.yelp_url)} target="_blank">View {label}</a>,
    },
    */
    {
        label: 'Demographics',
        conditional: object => !!object.is_veteran || !!object.is_minority || !!object.is_female || !!object.is_lgbt,
        content: (object) => (<>
            {!!object.is_veteran && <Typography sx={{display: 'block'}}>Veteran</Typography>}
            {!!object.is_minority && <Typography sx={{display: 'block'}}>Minority</Typography>}
            {!!object.is_female && <Typography sx={{display: 'block'}}>Woman</Typography>}
            {!!object.is_lgbt && <Typography sx={{display: 'block'}}>LGBT</Typography>}
        </>),
    },
];

export function ContactInfoShow() {
    return (
        <ObjectPrimaryInfo
            title={'Contact Info'}
            tint={'contact'}
            fields={dataToDisplay}
            cardHeader={<CardHeader/>}
        />
    )
}
